import { axios } from './request'

let url = import.meta.env.VITE_API_URL
if (url === '') {
  url = window.location.protocol + '//api.' + window.location.host
}

// A single dropped socket (a backgrounded tab, a brief mobile radio blip) must
// not raise the "lost contact" overlay (#260). Only surface it after this many
// failed reconnect attempts in a row, and never while the tab is hidden.
export const WS_UNREACHABLE_THRESHOLD = 4

// The threshold above counts attempts, but what it is meant to buy is TIME.
// Without a delay between attempts a refused handshake against a live host
// resolves in a couple of milliseconds, so all four are spent long before any
// real blip is over and the threshold guards nothing (#297). Backing off makes
// the count mean seconds again, and keeps a client from hammering a server that
// is still booting behind the #270 readiness gate.
//
// The first retry stays immediate, so a genuine one-frame drop still reconnects
// unnoticed; from there each attempt doubles up to the cap. Four attempts now
// span ~3.5s, and a server that stays down is polled ~8 times in the first
// minute instead of hundreds.
export const RECONNECT_BASE_MS = 500
export const RECONNECT_CAP_MS = 30000
const PING_INTERVAL_MS = 55000

// Equal jitter: half the delay fixed, half random. A deploy drops every client's
// socket at the same instant, so undithered retries arrive back in synchronised
// bursts. Full jitter (random across the whole delay) would spread them wider
// but can collapse a delay to near zero — re-creating #297 by chance — so the
// floor stays at half the nominal delay and only the remainder is dithered.
export function reconnectDelay(attempt) {
  if (attempt < 1) {
    return 0
  }
  const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, attempt - 1), RECONNECT_CAP_MS)
  return delay / 2 + Math.random() * (delay / 2)
}

export const websocket = {
  conn: null,
  connected: false,
  retryCount: 0,
  retryTimer: null,
  init() {
    if (window['WebSocket']) {
      if (Object.keys(window.vue.$store.state.auth.jwt).length > 0) {
        this.connect()
      }
    }
  },
  connect() {
    // Drop any scheduled retry and detach the previous socket first. connect()
    // is also a public "reconnect now" entry (TreasuryDocs/Multiple.vue), and a
    // superseded socket's onclose would otherwise clear `connected` and start a
    // second reconnect chain running behind this one.
    this.cancelRetry()
    this.discard(this.conn)

    const wsUrl = url.replace(/^http/, 'ws')
    const socket = new WebSocket(wsUrl + '/websocket')
    this.conn = socket
    this.retryCount += 1

    socket.onopen = () => {
      if (this.conn !== socket) return
      this.connected = true
      this.retryCount = 0
      this.sendPing(socket)
    }

    socket.onclose = () => {
      if (this.conn !== socket) return
      this.connected = false
      this.reportUnreachable()
      this.scheduleReconnect()
    }

    socket.onmessage = (evt) => {
      if (this.conn !== socket) return
      const data = JSON.parse(evt.data)
      const event = new CustomEvent('ws:' + data.topic, { detail: data.payload })
      window.dispatchEvent(event)
    }
  },
  discard(socket) {
    if (!socket) return
    socket.onopen = null
    socket.onclose = null
    socket.onmessage = null
    socket.close()
  },
  cancelRetry() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  },
  scheduleReconnect() {
    this.cancelRetry()
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.connect()
    }, reconnectDelay(this.retryCount))
  },
  // Raising the overlay is all this module does; clearing it stays with
  // ServerStatus.vue, whose HTTP probe is what confirms the server is back.
  reportUnreachable() {
    if (this.connected) return
    if (document.visibilityState === 'hidden') return
    if (this.retryCount < WS_UNREACHABLE_THRESHOLD) return
    window.vue.$store.dispatch('serverStatus', 'unreacheable')
  },
  // A hidden tab never raises the overlay, and the browser throttles its timers,
  // so a tab coming back can be deep into the backoff with nothing on screen to
  // show for it. Re-check, and retry now rather than waiting out a delay
  // computed while nobody was watching. retryCount is deliberately NOT reset —
  // that would put the overlay back below the threshold and suppress it again.
  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') return
    if (this.connected || !this.conn) return
    this.reportUnreachable()
    this.connect()
  },
  sendPing(socket) {
    setTimeout(() => {
      // Only the chain belonging to the live socket survives. A chain left from
      // an earlier connection would otherwise keep pinging through whatever
      // socket is current, one extra ping per interval for every reconnect.
      if (this.conn !== socket || !this.connected) return
      socket.send(JSON.stringify({ topic: 'ping' }))
      this.sendPing(socket)
    }, PING_INTERVAL_MS)
  },
}

document.addEventListener('visibilitychange', () => websocket.handleVisibilityChange())

window.addEventListener('ws:handshake', (data) => {
  if (Object.keys(window.vue.$store.state.auth.jwt).length > 0) {
    axios
      .post(url + '/websocket/auth', { connectionId: data.detail })
      .then(() => {})
      .catch((err) => {
        console.log(err)
      })
  }
})

window.addEventListener('ws:debug', (data) => {
  window.vue.$toasted.show(data.detail).goAway(3000)
})

window.addEventListener('ws:pronobisTransactions/parseErrors', (event) => {
  if (!window.vue || !window.vue.$store) return
  try {
    const payload = JSON.parse(event.detail)
    window.vue.$store.dispatch('pronobisImportStatus/handleParseErrorsEvent', payload)
  } catch {
    // Ignore malformed payloads — the banner reflects the last valid state.
  }
})

window.addEventListener('ws:pronobisTransactions/statusDismissed', () => {
  if (!window.vue || !window.vue.$store) return
  window.vue.$store.dispatch('pronobisImportStatus/handleDismissedEvent')
})

// A payload-less ping telling the client to re-fetch its actionable counts so
// the bell badge and inline nav counts stay live (#199). No toast or inbox state.
window.addEventListener('ws:counts:refresh', () => {
  if (window.vue && window.vue.$store) {
    window.vue.$store.dispatch('actionableCounts/refresh')
  }
})
